import { fullyDecodeRouteParam, normalizeRoutePath } from '~/utils/route'

// Consume SSR payload on first read so subsequent fetches (e.g. page change) re-fetch normally.
// This is needed because defineAsyncComponent defers setup() past nuxtApp.isHydrating=false,
// so useAsyncData would otherwise ignore the SSR payload and re-fetch immediately.
function ssrPayloadCache(key: string, nuxtApp: ReturnType<typeof useNuxtApp>) {
  const data = nuxtApp.payload.data[key]
  if (data !== undefined) {
    delete nuxtApp.payload.data[key]
    return data
  }
  return nuxtApp.static?.data?.[key]
}

export const useListApi = async <T>({
  itemPath,
  hasCategory,
  categoryApiPath,
  categoryField,
  categoryQueryMode = 'filters',
  componentName,
  defaultPerPage = 10,
  onFetchSuccess = () => {},
  onFetchError = () => {},
}: DataWithCategoryFilter) => {
  const CATEGORY_FETCH_LIMIT = 1000
  const nuxtApp = useNuxtApp()
  const { localePath, currentLocaleCode } = useLanguage()
  const route = useRoute()
  const { t } = useI18n()
  const categorySlug = computed(() => fullyDecodeRouteParam(String(route.params.categorySlug || '')))
  const menuSlug = computed(() => fullyDecodeRouteParam(String(route.params.slug || '')))
  const { getPath, slugPageInfo, getBreadcrumb } = useNavigationMenu()

  if (route.params.slug && !slugPageInfo.value) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found' })
  }

  const $api = nuxtApp.$api as typeof $fetch
  const routePathKey = normalizeRoutePath(String(route.path))
  const total = useState<number>(`total-${routePathKey}`, () => 0)
  const perPage = ref<number>(defaultPerPage || 10)
  const page = ref<number>(parseInt(route.query.page as string, 10) || 1)

  const loadPageData = async (currentPage: number) => {
    let section: Section = {
      title: '',
      description: '',
    }
    let category: Category | null = null
    let categoryButtonItems: CategoryMenuItem[] = []

    if (hasCategory && categoryApiPath) {
      const response = await $api<ApiSuccessResponse<Category[]>>(categoryApiPath, {
        query: {
          page: 1,
          perPage: CATEGORY_FETCH_LIMIT,
          orderBy: 'position',
          sortBy: 'asc',
        },
      })
      const rawCategories = response?.data
      const categories: Category[] = Array.isArray(rawCategories)
        ? rawCategories
        : (rawCategories as unknown as { data: Category[] })?.data || []

      if (categorySlug.value) {
        const matchedCategory = categories.find(item => item.slug === categorySlug.value)
        if (!matchedCategory || typeof matchedCategory === 'undefined') {
          throw createError({ statusCode: 404, message: 'Page not found' })
        }
        category = matchedCategory as Category
      }

      categoryButtonItems = [
        {
          title: t('global.total', {}, { locale: currentLocaleCode.value }),
          path: localePath(`/${menuSlug.value}`),
        } as CategoryMenuItem,
        ...categories
          .filter(item => item.title !== '' && item.slug !== '')
          .map(item => ({
            title: item.title,
            path: getPath({
              componentName,
              categorySlug: item.slug,
            }),
          }) as CategoryMenuItem),
      ]

      section = {
        title: `${slugPageInfo.value?.topic ? slugPageInfo.value.topic : slugPageInfo.value?.title}${category ? ` - ${category.title}` : ''}`,
        description: slugPageInfo.value?.description || '',
      }
    } else {
      section = {
        title: slugPageInfo.value?.topic || slugPageInfo.value?.title || '',
        description: slugPageInfo.value?.description || '',
      }
    }

    const itemQuery: Record<string, unknown> = {
      page: currentPage,
      perPage: defaultPerPage,
      status: 'published',
    }
    if (typeof categoryField === 'string') {
      if (categoryQueryMode === 'direct') {
        itemQuery[categoryField] = category?._id
      } else {
        itemQuery[`filters[${categoryField}]`] = category?._id
      }
    }

    const statusStore = useStatusStore()
    const itemsResponse = await $api(itemPath, { query: itemQuery }) as Record<string, unknown>
    statusStore.updatePreloading(false)

    const itemsData = (itemsResponse.data || []) as T[]

    if (itemsData.length && onFetchSuccess) {
      onFetchSuccess(itemsData)
    }
    if (onFetchError && !itemsData.length) {
      onFetchError(new Error('Failed to fetch items'))
    }

    return {
      asyncCategory: { section, category, categoryButtonItems },
      items: itemsData,
      total: (itemsResponse.total as number) || 0,
      perPage: (itemsResponse.perPage as number) || defaultPerPage,
    }
  }

  // Single useAsyncData call for both category and initial items — avoids the
  // "Component is already mounted" warning that fires when a second useAsyncData
  // is called after an await inside a defineAsyncComponent setup context, because
  // Vue's getCurrentInstance() tracking is lost across async boundaries.
  const { data: ssrData } = await nuxtApp.runWithContext(() =>
    useAsyncData(`ssr-page-${routePathKey}`, async () => {
      const initialPage = parseInt(route.query.page as string, 10) || 1
      return loadPageData(initialPage)
    }, {
      getCachedData: (key, nuxtApp) => ssrPayloadCache(key, nuxtApp),
    }),
  )

  // Derive reactive state from SSR data
  const asyncCategory = ref(ssrData.value?.asyncCategory ?? null)
  const items = ref<T[]>((ssrData.value?.items ?? []) as T[])
  total.value = ssrData.value?.total ?? 0
  perPage.value = ssrData.value?.perPage ?? defaultPerPage

  const breadcrumbs = computed(() => getBreadcrumb(asyncCategory.value?.category?.title))

  // Only sync URL page query to local page value, no automatic navigation
  if (import.meta.client) {
    watch(
      () => route.query.page,
      (newPage) => {
        const newPageValue = parseInt(newPage as string, 10) || 1
        if (page.value !== newPageValue) {
          page.value = newPageValue
        }
      },
      { immediate: true },
    )

    // Re-fetch items on page change using $fetch directly (post-mount client-side navigation)
    watch(page, async (newPage, oldPage) => {
      if (newPage === oldPage) return

      const response = await loadPageData(newPage)
      asyncCategory.value = response.asyncCategory
      total.value = response.total
      perPage.value = response.perPage
      items.value = response.items
    })

    watch([categorySlug, menuSlug], async ([newCategorySlug, newMenuSlug], [oldCategorySlug, oldMenuSlug]) => {
      if (newCategorySlug === oldCategorySlug && newMenuSlug === oldMenuSlug) return

      const newPage = parseInt(route.query.page as string, 10) || 1
      if (page.value !== newPage) {
        page.value = newPage
        return
      }

      const response = await loadPageData(newPage)
      asyncCategory.value = response.asyncCategory
      total.value = response.total
      perPage.value = response.perPage
      items.value = response.items
    })
  }

  const goToPage = (pageNumber: number) => {
    page.value = pageNumber
    navigateTo({
      query: { ...route.query, page: pageNumber.toString() },
    })
  }

  return {
    breadcrumbs,
    items,
    asyncCategory,
    page,
    total,
    perPage,
    goToPage,
  }
}
